import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Switch } from './ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Building2,
  Briefcase,
  User,
  Calendar,
  Plus,
  Trash2,
  Edit,
  Check,
  X,
  ChevronDown,
  ChevronRight,
  Clock,
  MessageSquare,
  Send,
  Sparkles,
  FileText,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Settings,
  Save,
  RefreshCw,
  Mail,
  Star,
  Users
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ============ Admin Configuration Panel Component ============
export const HRAssessmentConfigPanel = ({ getAuthToken, companies = [] }) => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingConfig, setEditingConfig] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  
  const [configForm, setConfigForm] = useState({
    company_id: '',
    company_name: '',
    position_id: '',
    position_name: '',
    default_briefing_sessions: 1,
    default_demo_rounds: 1,
    briefing_topics: ['Company Overview', 'Role Responsibilities', 'Process & Expectations'],
    demo_checkpoints: [
      { name: 'English Communication', description: 'Clarity and fluency in communication' },
      { name: 'Knowledge About Process', description: 'Understanding of role requirements' },
      { name: 'Readiness & Attitude', description: 'Preparedness and professional demeanor' },
      { name: 'Problem Solving', description: 'Ability to handle scenarios' }
    ],
    is_active: true
  });

  const [positions, setPositions] = useState([]);
  const [loadingPositions, setLoadingPositions] = useState(false);

  const fetchConfigs = useCallback(async () => {
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API}/api/hr-assessment-configs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfigs(res.data || []);
    } catch (error) {
      console.error('Failed to fetch configs:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthToken]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  const fetchPositionsForCompany = async (companyId, companyName) => {
    if (!companyName) {
      setPositions([]);
      return;
    }
    setLoadingPositions(true);
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API}/api/job-updates/by-company/${encodeURIComponent(companyName)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter out positions without a valid position_name
      const validPositions = (res.data || []).filter(p => p.position_name && p.position_name.trim());
      setPositions(validPositions);
    } catch (error) {
      setPositions([]);
    } finally {
      setLoadingPositions(false);
    }
  };

  const handleSaveConfig = async () => {
    if (!configForm.company_name) {
      toast.error('Please select a company');
      return;
    }

    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      if (editingConfig) {
        await axios.patch(`${API}/api/hr-assessment-configs/${editingConfig.id}`, configForm, config);
        toast.success('Configuration updated');
      } else {
        await axios.post(`${API}/api/hr-assessment-configs`, configForm, config);
        toast.success('Configuration created');
      }
      
      setIsCreating(false);
      setEditingConfig(null);
      resetForm();
      fetchConfigs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save configuration');
    }
  };

  const handleDeleteConfig = async (configId) => {
    if (!window.confirm('Delete this configuration?')) return;
    try {
      const token = getAuthToken();
      await axios.delete(`${API}/api/hr-assessment-configs/${configId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Configuration deleted');
      fetchConfigs();
    } catch (error) {
      toast.error('Failed to delete configuration');
    }
  };

  const resetForm = () => {
    setConfigForm({
      company_id: '',
      company_name: '',
      position_id: '',
      position_name: '',
      default_briefing_sessions: 1,
      default_demo_rounds: 1,
      briefing_topics: ['Company Overview', 'Role Responsibilities', 'Process & Expectations'],
      demo_checkpoints: [
        { name: 'English Communication', description: 'Clarity and fluency in communication' },
        { name: 'Knowledge About Process', description: 'Understanding of role requirements' },
        { name: 'Readiness & Attitude', description: 'Preparedness and professional demeanor' },
        { name: 'Problem Solving', description: 'Ability to handle scenarios' }
      ],
      is_active: true
    });
    setPositions([]);
  };

  const addBriefingTopic = () => {
    setConfigForm(prev => ({
      ...prev,
      briefing_topics: [...prev.briefing_topics, '']
    }));
  };

  const removeBriefingTopic = (index) => {
    setConfigForm(prev => ({
      ...prev,
      briefing_topics: prev.briefing_topics.filter((_, i) => i !== index)
    }));
  };

  const updateBriefingTopic = (index, value) => {
    setConfigForm(prev => ({
      ...prev,
      briefing_topics: prev.briefing_topics.map((t, i) => i === index ? value : t)
    }));
  };

  const addDemoCheckpoint = () => {
    setConfigForm(prev => ({
      ...prev,
      demo_checkpoints: [...prev.demo_checkpoints, { name: '', description: '' }]
    }));
  };

  const removeDemoCheckpoint = (index) => {
    setConfigForm(prev => ({
      ...prev,
      demo_checkpoints: prev.demo_checkpoints.filter((_, i) => i !== index)
    }));
  };

  const updateDemoCheckpoint = (index, field, value) => {
    setConfigForm(prev => ({
      ...prev,
      demo_checkpoints: prev.demo_checkpoints.map((c, i) => 
        i === index ? { ...c, [field]: value } : c
      )
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="h-6 w-6 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-800">Assessment Configuration</h3>
          <p className="text-sm text-slate-500">Configure briefing topics and demo checkpoints per company/position</p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setIsCreating(true);
            setEditingConfig(null);
          }}
          className="rounded-xl bg-gradient-to-r from-blue-500 to-indigo-600"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Configuration
        </Button>
      </div>

      {/* Configuration Form Dialog */}
      <Dialog open={isCreating || !!editingConfig} onOpenChange={(open) => {
        if (!open) {
          setIsCreating(false);
          setEditingConfig(null);
          resetForm();
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5 text-blue-600" />
              {editingConfig ? 'Edit Configuration' : 'New Assessment Configuration'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Company & Position Selection */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Company *</Label>
                <Select
                  value={configForm.company_id || ''}
                  onValueChange={(value) => {
                    const company = companies.find(c => c.id === value);
                    setConfigForm(prev => ({
                      ...prev,
                      company_id: value,
                      company_name: company?.name || company?.company_name || '',
                      position_id: '',
                      position_name: ''
                    }));
                    if (company) {
                      fetchPositionsForCompany(value, company.name || company.company_name);
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select company" />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-slate-400" />
                          {c.name || c.company_name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Position (Optional)</Label>
                {positions.length > 0 ? (
                  <Select
                    value={configForm.position_id || '__all_positions__'}
                    onValueChange={(value) => {
                      // Radix Select.Item cannot have an empty-string value (that's reserved
                      // for clearing the selection), so "All positions" uses a sentinel value
                      // here and is translated back to '' in configForm, matching the
                      // pre-existing submitted shape.
                      if (value === '__all_positions__') {
                        setConfigForm(prev => ({
                          ...prev,
                          position_id: '',
                          position_name: ''
                        }));
                        return;
                      }
                      const position = positions.find(p => p.id === value);
                      setConfigForm(prev => ({
                        ...prev,
                        position_id: value,
                        position_name: position?.position_name || ''
                      }));
                    }}
                  >
                    <SelectTrigger className={loadingPositions ? 'opacity-50' : ''}>
                      <SelectValue placeholder={loadingPositions ? "Loading..." : "All positions"} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all_positions__">All positions</SelectItem>
                      {positions.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.position_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    value={configForm.position_name}
                    onChange={(e) => setConfigForm(prev => ({ ...prev, position_name: e.target.value }))}
                    placeholder="Enter position name"
                  />
                )}
              </div>
            </div>

            {/* Default Counts */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Default Briefing Sessions</Label>
                <Select
                  value={String(configForm.default_briefing_sessions)}
                  onValueChange={(v) => setConfigForm(prev => ({ ...prev, default_briefing_sessions: parseInt(v) }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5].map(n => (
                      <SelectItem key={n} value={String(n)}>{n} session{n > 1 ? 's' : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Default Demo HR Rounds</Label>
                <Select
                  value={String(configForm.default_demo_rounds)}
                  onValueChange={(v) => setConfigForm(prev => ({ ...prev, default_demo_rounds: parseInt(v) }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5].map(n => (
                      <SelectItem key={n} value={String(n)}>{n} round{n > 1 ? 's' : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Briefing Topics */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Briefing Topics</Label>
                <Button type="button" variant="outline" size="sm" onClick={addBriefingTopic}>
                  <Plus className="h-3 w-3 mr-1" /> Add Topic
                </Button>
              </div>
              <div className="space-y-2">
                {configForm.briefing_topics.map((topic, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <span className="text-sm text-slate-500 w-6">{index + 1}.</span>
                    <Input
                      value={topic}
                      onChange={(e) => updateBriefingTopic(index, e.target.value)}
                      placeholder="Enter topic name"
                      className="flex-1"
                    />
                    {configForm.briefing_topics.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeBriefingTopic(index)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Demo Checkpoints */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Demo HR Round Checkpoints</Label>
                <Button type="button" variant="outline" size="sm" onClick={addDemoCheckpoint}>
                  <Plus className="h-3 w-3 mr-1" /> Add Checkpoint
                </Button>
              </div>
              <div className="space-y-3">
                {configForm.demo_checkpoints.map((checkpoint, index) => (
                  <div key={index} className="p-3 border rounded-lg bg-slate-50 space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-500 w-6">{index + 1}.</span>
                      <Input
                        value={checkpoint.name}
                        onChange={(e) => updateDemoCheckpoint(index, 'name', e.target.value)}
                        placeholder="Checkpoint name"
                        className="flex-1"
                      />
                      {configForm.demo_checkpoints.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeDemoCheckpoint(index)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    <Input
                      value={checkpoint.description}
                      onChange={(e) => updateDemoCheckpoint(index, 'description', e.target.value)}
                      placeholder="Description (optional)"
                      className="ml-8 text-sm"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Active Toggle */}
            <div className="flex items-center gap-3">
              <Switch
                checked={configForm.is_active}
                onCheckedChange={(checked) => setConfigForm(prev => ({ ...prev, is_active: checked }))}
              />
              <Label>Active configuration</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsCreating(false);
              setEditingConfig(null);
              resetForm();
            }}>
              Cancel
            </Button>
            <Button onClick={handleSaveConfig} className="bg-blue-600 hover:bg-blue-700">
              <Save className="h-4 w-4 mr-2" />
              {editingConfig ? 'Update' : 'Save'} Configuration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Existing Configurations List */}
      <div className="grid gap-4">
        {configs.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <Settings className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No configurations yet</p>
              <p className="text-sm text-slate-400 mt-1">Create a configuration to define assessment structure per company/position</p>
            </CardContent>
          </Card>
        ) : (
          configs.map(config => (
            <Card key={config.id} className={`${!config.is_active ? 'opacity-60' : ''}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <Building2 className="h-5 w-5 text-blue-600" />
                      <span className="font-semibold text-slate-800">{config.company_name}</span>
                      {config.position_name && (
                        <>
                          <span className="text-slate-400">•</span>
                          <Badge variant="secondary">{config.position_name}</Badge>
                        </>
                      )}
                      {!config.is_active && (
                        <Badge variant="outline" className="text-slate-500">Inactive</Badge>
                      )}
                    </div>
                    <div className="flex gap-4 text-sm text-slate-600">
                      <span>{config.default_briefing_sessions} briefing{config.default_briefing_sessions > 1 ? 's' : ''}</span>
                      <span>{config.default_demo_rounds} demo round{config.default_demo_rounds > 1 ? 's' : ''}</span>
                      <span>{config.briefing_topics?.length || 0} topics</span>
                      <span>{config.demo_checkpoints?.length || 0} checkpoints</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setConfigForm({
                          ...config,
                          briefing_topics: config.briefing_topics || [],
                          demo_checkpoints: config.demo_checkpoints || []
                        });
                        setEditingConfig(config);
                        if (config.company_name) {
                          fetchPositionsForCompany(config.company_id, config.company_name);
                        }
                      }}
                    >
                      <Edit className="h-4 w-4 mr-1" /> Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteConfig(config.id)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

// ============ Add Performance Dialog Component ============
export const AddPerformanceDialog = ({ 
  isOpen, 
  onClose, 
  candidate,
  getAuthToken,
  companies = [],
  onSave,
  existingPerformance = null // Optional: pass existing performance data for editing
}) => {
  const [step, setStep] = useState(1); // 1: Company/Position, 2: Sessions
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [positions, setPositions] = useState([]);
  const [loadingPositions, setLoadingPositions] = useState(false);
  const [config, setConfig] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [loadedPerformance, setLoadedPerformance] = useState(null);
  
  // Session state
  const [briefingSessions, setBriefingSessions] = useState([]);
  const [demoRounds, setDemoRounds] = useState([]);
  const [extraSessionReason, setExtraSessionReason] = useState('');
  const [showAddExtraDialog, setShowAddExtraDialog] = useState({ type: null, show: false });
  
  const [saving, setSaving] = useState(false);

  // Fetch existing performance data for this candidate
  const fetchExistingPerformance = async () => {
    if (!candidate?.id) return null;
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API}/api/candidate-performance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Find performance for this candidate
      const existing = res.data?.find(p => p.candidate_status_id === candidate.id);
      return existing || null;
    } catch (error) {
      console.error('Error fetching existing performance:', error);
      return null;
    }
  };

  // Load existing data into session state
  const loadExistingDataIntoSessions = (perfData, cfg) => {
    if (!perfData) return;
    
    const topics = cfg?.briefing_topics || ['Company Overview', 'Role Responsibilities', 'Process & Expectations'];
    const checkpoints = cfg?.demo_checkpoints || [
      { name: 'English Communication', description: '' },
      { name: 'Knowledge About Process', description: '' },
      { name: 'Readiness & Attitude', description: '' }
    ];
    
    // Load briefing sessions from existing data
    const loadedBriefings = [];
    
    // Check briefing_sessions array first (newer format)
    if (perfData.briefing_sessions && perfData.briefing_sessions.length > 0) {
      perfData.briefing_sessions.forEach((session, idx) => {
        loadedBriefings.push({
          id: `briefing_${idx + 1}`,
          session_number: idx + 1,
          topics: session.topics || topics.map(t => ({ name: t, covered: false })),
          feedback: session.feedback || '',
          status: session.status || 'pending',
          attendance: session.attendance || 'pending',
          scheduled_datetime: session.scheduled_datetime ? session.scheduled_datetime.slice(0, 16) : '',
          ai_suggestion: session.ai_suggestion || '',
          is_extra: session.is_extra || false,
          extra_reason: session.extra_reason || ''
        });
      });
    }
    
    // If no briefings found, create default empty one
    if (loadedBriefings.length === 0) {
      const defaultCount = cfg?.default_briefing_sessions || 1;
      for (let i = 0; i < defaultCount; i++) {
        loadedBriefings.push({
          id: `briefing_${i + 1}`,
          session_number: i + 1,
          topics: topics.map(t => ({ name: t, covered: false })),
          feedback: '',
          status: 'pending',
          attendance: 'pending',
          scheduled_datetime: '',
          ai_suggestion: '',
          is_extra: false,
          extra_reason: ''
        });
      }
    }
    
    // Load demo rounds from existing data
    const loadedDemos = [];
    
    // Check demo_rounds array first (newer format)
    if (perfData.demo_rounds && perfData.demo_rounds.length > 0) {
      perfData.demo_rounds.forEach((round, idx) => {
        loadedDemos.push({
          id: `demo_${idx + 1}`,
          round_number: idx + 1,
          checkpoints: round.checkpoints?.length ? round.checkpoints.map(c => ({
            name: c.name,
            description: c.description || '',
            met: c.met === true ? true : c.met === false ? false : null,
            notes: c.notes || ''
          })) : checkpoints.map(c => ({ 
            name: c.name, 
            description: c.description || '',
            met: null, 
            notes: '' 
          })),
          feedback: round.feedback || '',
          status: round.status || 'pending',
          scheduled_datetime: round.scheduled_datetime ? round.scheduled_datetime.slice(0, 16) : '',
          is_extra: round.is_extra || false,
          extra_reason: round.extra_reason || ''
        });
      });
    }
    
    // If no demos found, create default empty one
    if (loadedDemos.length === 0) {
      const defaultCount = cfg?.default_demo_rounds || 1;
      for (let i = 0; i < defaultCount; i++) {
        loadedDemos.push({
          id: `demo_${i + 1}`,
          round_number: i + 1,
          checkpoints: checkpoints.map(c => ({ 
            name: c.name, 
            description: c.description || '',
            met: null, 
            notes: '' 
          })),
          feedback: '',
          status: 'pending',
          scheduled_datetime: '',
          is_extra: false,
          extra_reason: ''
        });
      }
    }
    
    setBriefingSessions(loadedBriefings);
    setDemoRounds(loadedDemos);
  };

  // Reset when dialog opens
  useEffect(() => {
    if (isOpen && candidate) {
      setStep(1);
      setIsEditMode(false);
      setLoadedPerformance(null);
      
      // Pre-select company if candidate has one
      if (candidate.company_name) {
        const company = companies.find(c => 
          (c.name || c.company_name)?.toLowerCase() === candidate.company_name?.toLowerCase()
        );
        if (company) {
          setSelectedCompany(company);
          fetchPositionsForCompany(company);
        }
      }
      
      // Check for existing performance data
      fetchExistingPerformance().then(existing => {
        if (existing) {
          setIsEditMode(true);
          setLoadedPerformance(existing);
          // Pre-select position if available
          if (existing.position_name) {
            setSelectedPosition({ position_name: existing.position_name });
          }
        }
      });
    }
  }, [isOpen, candidate, companies]);

  const fetchPositionsForCompany = async (company) => {
    if (!company) return;
    setLoadingPositions(true);
    try {
      const token = getAuthToken();
      const companyName = company.name || company.company_name;
      const res = await axios.get(`${API}/api/job-updates/by-company/${encodeURIComponent(companyName)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPositions(res.data || []);
    } catch (error) {
      setPositions([]);
    } finally {
      setLoadingPositions(false);
    }
  };

  const fetchConfigForSelection = async (companyName, positionName) => {
    setLoadingConfig(true);
    try {
      const token = getAuthToken();
      const params = new URLSearchParams();
      params.append('company_name', companyName);
      if (positionName) params.append('position_name', positionName);
      
      const res = await axios.get(`${API}/api/hr-assessment-configs/for-selection?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const cfg = res.data;
      setConfig(cfg);
      
      // If we have existing performance data (edit mode), load it
      if (isEditMode && loadedPerformance) {
        loadExistingDataIntoSessions(loadedPerformance, cfg);
      } else {
        // Initialize empty sessions based on config
        const defaultBriefings = cfg?.default_briefing_sessions || 1;
        const defaultDemos = cfg?.default_demo_rounds || 1;
        const topics = cfg?.briefing_topics || ['Company Overview', 'Role Responsibilities', 'Process & Expectations'];
        const checkpoints = cfg?.demo_checkpoints || [
          { name: 'English Communication', description: '' },
          { name: 'Knowledge About Process', description: '' },
          { name: 'Readiness & Attitude', description: '' }
        ];
        
        // Create briefing sessions
        const newBriefings = Array.from({ length: defaultBriefings }, (_, i) => ({
          id: `briefing_${i + 1}`,
          session_number: i + 1,
          topics: topics.map(t => ({ name: t, covered: false })),
          feedback: '',
          status: 'pending',
          attendance: 'pending',
          scheduled_datetime: '',
          ai_suggestion: '',
          is_extra: false,
          extra_reason: ''
        }));
        
        // Create demo rounds
        const newDemos = Array.from({ length: defaultDemos }, (_, i) => ({
          id: `demo_${i + 1}`,
          round_number: i + 1,
          checkpoints: checkpoints.map(c => ({ 
            name: c.name, 
            description: c.description || '',
            met: null, 
            notes: '' 
          })),
          feedback: '',
          status: 'pending',
          scheduled_datetime: '',
          is_extra: false,
          extra_reason: ''
        }));
        
        setBriefingSessions(newBriefings);
        setDemoRounds(newDemos);
      }
      
    } catch (error) {
      console.error('No config found, using defaults');
      // Use defaults - still check for existing data
      setConfig(null);
      
      if (isEditMode && loadedPerformance) {
        loadExistingDataIntoSessions(loadedPerformance, null);
      } else {
        setBriefingSessions([{
          id: 'briefing_1',
          session_number: 1,
          topics: [
            { name: 'Company Overview', covered: false },
            { name: 'Role Responsibilities', covered: false },
            { name: 'Process & Expectations', covered: false }
          ],
          feedback: '',
          status: 'pending',
          attendance: 'pending',
          scheduled_datetime: '',
          ai_suggestion: '',
          is_extra: false,
          extra_reason: ''
        }]);
        setDemoRounds([{
          id: 'demo_1',
          round_number: 1,
          checkpoints: [
            { name: 'English Communication', description: '', met: null, notes: '' },
            { name: 'Knowledge About Process', description: '', met: null, notes: '' },
            { name: 'Readiness & Attitude', description: '', met: null, notes: '' }
          ],
          feedback: '',
          status: 'pending',
          scheduled_datetime: '',
          is_extra: false,
          extra_reason: ''
        }]);
      }
    } finally {
      setLoadingConfig(false);
    }
  };

  const handleCompanySelect = (company) => {
    setSelectedCompany(company);
    setSelectedPosition(null);
    fetchPositionsForCompany(company);
  };

  const handlePositionSelect = (position) => {
    setSelectedPosition(position);
  };

  const handleProceedToSessions = () => {
    if (!selectedCompany) {
      toast.error('Please select a company');
      return;
    }
    fetchConfigForSelection(
      selectedCompany.name || selectedCompany.company_name,
      selectedPosition?.position_name || ''
    );
    setStep(2);
  };

  const handleAddExtraSession = (type) => {
    if (!extraSessionReason.trim()) {
      toast.error('Please provide a reason for adding an extra session');
      return;
    }
    
    if (type === 'briefing') {
      const newSession = {
        id: `briefing_${briefingSessions.length + 1}`,
        session_number: briefingSessions.length + 1,
        topics: (config?.briefing_topics || ['Company Overview', 'Role Responsibilities']).map(t => ({ name: t, covered: false })),
        feedback: '',
        status: 'pending',
        attendance: 'pending',
        scheduled_datetime: '',
        ai_suggestion: '',
        is_extra: true,
        extra_reason: extraSessionReason
      };
      setBriefingSessions([...briefingSessions, newSession]);
    } else {
      const checkpoints = config?.demo_checkpoints || [
        { name: 'English Communication', description: '' },
        { name: 'Knowledge About Process', description: '' }
      ];
      const newRound = {
        id: `demo_${demoRounds.length + 1}`,
        round_number: demoRounds.length + 1,
        checkpoints: checkpoints.map(c => ({ name: c.name, description: c.description || '', met: null, notes: '' })),
        feedback: '',
        status: 'pending',
        scheduled_datetime: '',
        is_extra: true,
        extra_reason: extraSessionReason
      };
      setDemoRounds([...demoRounds, newRound]);
    }
    
    setShowAddExtraDialog({ type: null, show: false });
    setExtraSessionReason('');
    toast.success(`Extra ${type === 'briefing' ? 'briefing session' : 'demo round'} added`);
  };

  const handleSavePerformance = async () => {
    setSaving(true);
    try {
      const token = getAuthToken();
      
      // Helper to convert empty string dates to null
      const toDateOrNull = (dateStr) => dateStr || null;
      
      // Build flat structure compatible with Dashboard display
      const payload = {
        candidate_status_id: candidate.id,
        candidate_name: candidate.candidate_name || candidate.name || 'Unknown',
        company_name: selectedCompany?.name || selectedCompany?.company_name || candidate.company_name || '',
        process: selectedPosition?.position_name || candidate.process || '',
        briefing_sessions: briefingSessions,
        demo_rounds: demoRounds,
        config_id: config?.id || null
      };
      
      // Save to OLD candidate-performance endpoint (for Dashboard compatibility)
      await axios.post(`${API}/api/candidate-performance`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(isEditMode ? 'Performance record updated' : 'Performance record saved');
      onSave?.();
      onClose();
    } catch (error) {
      console.error('Error saving performance:', error.response?.data || error);
      const errorMsg = error.response?.data?.detail;
      // Handle Pydantic validation error format
      if (Array.isArray(errorMsg)) {
        toast.error(errorMsg[0]?.msg || 'Validation error');
      } else {
        toast.error(errorMsg || 'Failed to save performance');
      }
    } finally {
      setSaving(false);
    }
  };

  const updateBriefingSession = (sessionId, field, value) => {
    setBriefingSessions(prev => prev.map(s => 
      s.id === sessionId ? { ...s, [field]: value } : s
    ));
  };

  const updateBriefingTopic = (sessionId, topicIndex, covered) => {
    setBriefingSessions(prev => prev.map(s => {
      if (s.id === sessionId) {
        const newTopics = [...s.topics];
        newTopics[topicIndex] = { ...newTopics[topicIndex], covered };
        return { ...s, topics: newTopics };
      }
      return s;
    }));
  };

  const updateDemoRound = (roundId, field, value) => {
    setDemoRounds(prev => prev.map(r => 
      r.id === roundId ? { ...r, [field]: value } : r
    ));
  };

  const updateDemoCheckpoint = (roundId, checkpointIndex, field, value) => {
    setDemoRounds(prev => prev.map(r => {
      if (r.id === roundId) {
        const newCheckpoints = [...r.checkpoints];
        newCheckpoints[checkpointIndex] = { ...newCheckpoints[checkpointIndex], [field]: value };
        return { ...r, checkpoints: newCheckpoints };
      }
      return r;
    }));
  };

  if (!candidate) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${isEditMode ? 'bg-gradient-to-br from-amber-500 to-orange-600' : 'bg-gradient-to-br from-blue-500 to-indigo-600'}`}>
              <User className="h-5 w-5 text-white" />
            </div>
            <div>
              <span className="block">{candidate.candidate_name}</span>
              <span className="text-sm font-normal text-slate-500">
                {isEditMode ? (step === 1 ? 'Update Performance - Select Company & Position' : 'Update Performance - Edit Sessions') : (step === 1 ? 'Select Company & Position' : 'Configure Sessions')}
              </span>
            </div>
            {isEditMode && (
              <span className="ml-auto px-2 py-1 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">
                Editing Existing Data
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {step === 1 ? (
          // Step 1: Company & Position Selection
          <div className="space-y-6 py-4">
            {/* Company Selection */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Company *</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {companies.map(company => (
                  <button
                    key={company.id}
                    onClick={() => handleCompanySelect(company)}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      selectedCompany?.id === company.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-slate-200 hover:border-blue-300 hover:bg-slate-50'
                    }`}
                  >
                    <Building2 className={`h-5 w-5 mb-2 ${selectedCompany?.id === company.id ? 'text-blue-600' : 'text-slate-400'}`} />
                    <p className="font-medium text-slate-800 truncate">{company.name || company.company_name}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Position Selection */}
            {selectedCompany && (
              <div className="space-y-3">
                <Label className="text-base font-semibold">Position (Optional)</Label>
                {loadingPositions ? (
                  <div className="flex items-center gap-2 text-slate-500">
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    Loading positions...
                  </div>
                ) : positions.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <button
                      onClick={() => setSelectedPosition(null)}
                      className={`p-3 rounded-xl border-2 text-left transition-all ${
                        !selectedPosition
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-slate-200 hover:border-indigo-300'
                      }`}
                    >
                      <p className="font-medium text-slate-700">All Positions</p>
                    </button>
                    {positions.map(pos => (
                      <button
                        key={pos.id}
                        onClick={() => handlePositionSelect(pos)}
                        className={`p-3 rounded-xl border-2 text-left transition-all ${
                          selectedPosition?.id === pos.id
                            ? 'border-indigo-500 bg-indigo-50'
                            : 'border-slate-200 hover:border-indigo-300'
                        }`}
                      >
                        <p className="font-medium text-slate-700 truncate">{pos.position_name}</p>
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">No positions found for this company</p>
                )}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button 
                onClick={handleProceedToSessions}
                disabled={!selectedCompany}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Continue to Sessions
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            </DialogFooter>
          </div>
        ) : (
          // Step 2: Sessions Configuration
          <div className="space-y-6 py-4">
            {loadingConfig ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="h-6 w-6 animate-spin text-blue-500" />
                <span className="ml-2 text-slate-500">Loading assessment structure...</span>
              </div>
            ) : (
              <>
                {/* Selection Summary */}
                <div className="p-4 bg-slate-50 rounded-xl flex items-center gap-4">
                  <Building2 className="h-5 w-5 text-blue-600" />
                  <div>
                    <span className="font-medium">{selectedCompany?.name || selectedCompany?.company_name}</span>
                    {selectedPosition && (
                      <span className="text-slate-500"> • {selectedPosition.position_name}</span>
                    )}
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setStep(1)}
                    className="ml-auto"
                  >
                    Change
                  </Button>
                </div>

                {/* Tabs for Briefings and Demo Rounds */}
                <Tabs defaultValue="briefings" className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="briefings" className="gap-2">
                      <MessageSquare className="h-4 w-4" />
                      Briefing Sessions ({briefingSessions.length})
                    </TabsTrigger>
                    <TabsTrigger value="demos" className="gap-2">
                      <Users className="h-4 w-4" />
                      Demo HR Rounds ({demoRounds.length})
                    </TabsTrigger>
                  </TabsList>

                  {/* Briefing Sessions Tab */}
                  <TabsContent value="briefings" className="space-y-4 mt-4">
                    {briefingSessions.map((session, idx) => (
                      <BriefingSessionCard
                        key={session.id}
                        session={session}
                        index={idx}
                        onUpdate={(field, value) => updateBriefingSession(session.id, field, value)}
                        onUpdateTopic={(topicIdx, covered) => updateBriefingTopic(session.id, topicIdx, covered)}
                        getAuthToken={getAuthToken}
                      />
                    ))}
                    
                    <Button
                      variant="outline"
                      className="w-full border-dashed"
                      onClick={() => setShowAddExtraDialog({ type: 'briefing', show: true })}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Extra Briefing Session
                    </Button>
                  </TabsContent>

                  {/* Demo Rounds Tab */}
                  <TabsContent value="demos" className="space-y-4 mt-4">
                    {demoRounds.map((round, idx) => (
                      <DemoRoundCard
                        key={round.id}
                        round={round}
                        index={idx}
                        onUpdate={(field, value) => updateDemoRound(round.id, field, value)}
                        onUpdateCheckpoint={(cpIdx, field, value) => updateDemoCheckpoint(round.id, cpIdx, field, value)}
                      />
                    ))}
                    
                    <Button
                      variant="outline"
                      className="w-full border-dashed"
                      onClick={() => setShowAddExtraDialog({ type: 'demo', show: true })}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Extra Demo HR Round
                    </Button>
                  </TabsContent>
                </Tabs>

                <DialogFooter className="gap-2">
                  <Button variant="outline" onClick={() => setStep(1)}>
                    <ChevronDown className="h-4 w-4 mr-2 rotate-90" />
                    Back
                  </Button>
                  <Button 
                    onClick={handleSavePerformance}
                    disabled={saving}
                    className="bg-gradient-to-r from-blue-500 to-indigo-600"
                  >
                    {saving ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        {isEditMode ? 'Updating...' : 'Saving...'}
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        {isEditMode ? 'Update Performance' : 'Save Performance'}
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </>
            )}
          </div>
        )}

        {/* Add Extra Session Dialog */}
        <Dialog open={showAddExtraDialog.show} onOpenChange={(open) => {
          if (!open) {
            setShowAddExtraDialog({ type: null, show: false });
            setExtraSessionReason('');
          }
        }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                Add Extra {showAddExtraDialog.type === 'briefing' ? 'Briefing Session' : 'Demo HR Round'}
              </DialogTitle>
              <DialogDescription>
                Please provide a reason for adding an additional session
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Label>Reason / Justification *</Label>
              <Textarea
                value={extraSessionReason}
                onChange={(e) => setExtraSessionReason(e.target.value)}
                placeholder="Why is an extra session needed?"
                className="mt-2"
                rows={3}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddExtraDialog({ type: null, show: false })}>
                Cancel
              </Button>
              <Button 
                onClick={() => handleAddExtraSession(showAddExtraDialog.type)}
                disabled={!extraSessionReason.trim()}
              >
                Add Session
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};

// ============ Briefing Session Card Component ============
export const BriefingSessionCard = ({ session, index, onUpdate, onUpdateTopic, getAuthToken }) => {
  const [showAISuggestion, setShowAISuggestion] = useState(false);
  const [loadingAI, setLoadingAI] = useState(false);

  const getAISuggestion = async () => {
    setLoadingAI(true);
    try {
      const token = getAuthToken();
      const coveredCount = session.topics.filter(t => t.covered).length;
      const totalTopics = session.topics.length;
      
      const res = await axios.post(`${API}/api/ai/briefing-suggestion`, {
        topics_covered: coveredCount,
        total_topics: totalTopics,
        feedback: session.feedback
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      onUpdate('ai_suggestion', res.data.suggestion);
      setShowAISuggestion(true);
    } catch (error) {
      toast.error('Failed to get AI suggestion');
    } finally {
      setLoadingAI(false);
    }
  };

  return (
    <Card className={`${session.is_extra ? 'border-amber-200 bg-amber-50/30' : ''}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <div className={`h-8 w-8 rounded-lg flex items-center justify-center text-white text-sm font-bold ${
              session.is_extra 
                ? 'bg-gradient-to-br from-amber-500 to-orange-500' 
                : 'bg-gradient-to-br from-violet-500 to-purple-600'
            }`}>
              B{session.session_number}
            </div>
            Briefing Session {session.session_number}
            {session.is_extra && (
              <Badge className="bg-amber-100 text-amber-700 text-xs">Extra</Badge>
            )}
          </CardTitle>
          <Select
            value={session.status}
            onValueChange={(v) => onUpdate('status', v)}
          >
            <SelectTrigger className="w-32 h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="scheduled">Scheduled</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {session.is_extra && session.extra_reason && (
          <p className="text-xs text-amber-700 mt-2 p-2 bg-amber-100/50 rounded">
            <strong>Reason:</strong> {session.extra_reason}
          </p>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Schedule & Attendance */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label className="text-xs">Date & Time</Label>
            <Input
              type="datetime-local"
              value={session.scheduled_datetime}
              onChange={(e) => onUpdate('scheduled_datetime', e.target.value)}
              className="h-9"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Attendance</Label>
            <Select
              value={session.attendance}
              onValueChange={(v) => onUpdate('attendance', v)}
            >
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="present">Present</SelectItem>
                <SelectItem value="absent">Absent</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Topics to Cover */}
        <div className="space-y-2">
          <Label className="text-xs font-semibold">Topics to Cover</Label>
          <div className="space-y-2">
            {session.topics.map((topic, tidx) => (
              <div 
                key={tidx} 
                className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                  topic.covered 
                    ? 'bg-emerald-50 border-emerald-200' 
                    : 'bg-slate-50 border-slate-200'
                }`}
              >
                <span className="text-sm">{topic.name}</span>
                <button
                  onClick={() => onUpdateTopic(tidx, !topic.covered)}
                  className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                    topic.covered
                      ? 'bg-emerald-500 text-white'
                      : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                  }`}
                >
                  {topic.covered ? (
                    <>
                      <CheckCircle2 className="h-3 w-3" />
                      Covered
                    </>
                  ) : (
                    <>
                      <XCircle className="h-3 w-3" />
                      Not Covered
                    </>
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Feedback */}
        <div className="space-y-1">
          <Label className="text-xs font-semibold">Detailed Feedback</Label>
          <Textarea
            value={session.feedback}
            onChange={(e) => onUpdate('feedback', e.target.value)}
            placeholder="What was covered, how the candidate responded, additional support needed..."
            rows={3}
          />
        </div>

        {/* AI Suggestion Panel */}
        <div className="border-t pt-4">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-xs font-semibold flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-violet-500" />
              Officium AI Suggestion (Optional)
            </Label>
            <Button
              variant="outline"
              size="sm"
              onClick={getAISuggestion}
              disabled={loadingAI}
              className="h-7 text-xs"
            >
              {loadingAI ? (
                <RefreshCw className="h-3 w-3 animate-spin mr-1" />
              ) : (
                <Sparkles className="h-3 w-3 mr-1" />
              )}
              Get Suggestion
            </Button>
          </div>
          {session.ai_suggestion && (
            <div className="p-3 bg-violet-50 border border-violet-200 rounded-lg">
              <p className="text-sm text-violet-800">{session.ai_suggestion}</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// ============ Demo Round Card Component ============
export const DemoRoundCard = ({ round, index, onUpdate, onUpdateCheckpoint }) => {
  return (
    <Card className={`${round.is_extra ? 'border-amber-200 bg-amber-50/30' : ''}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <div className={`h-8 w-8 rounded-lg flex items-center justify-center text-white text-sm font-bold ${
              round.is_extra 
                ? 'bg-gradient-to-br from-amber-500 to-orange-500' 
                : 'bg-gradient-to-br from-blue-500 to-indigo-600'
            }`}>
              H{round.round_number}
            </div>
            Demo HR Round {round.round_number}
            {round.is_extra && (
              <Badge className="bg-amber-100 text-amber-700 text-xs">Extra</Badge>
            )}
          </CardTitle>
          <Select
            value={round.status}
            onValueChange={(v) => onUpdate('status', v)}
          >
            <SelectTrigger className="w-32 h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="scheduled">Scheduled</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {round.is_extra && round.extra_reason && (
          <p className="text-xs text-amber-700 mt-2 p-2 bg-amber-100/50 rounded">
            <strong>Reason:</strong> {round.extra_reason}
          </p>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Schedule */}
        <div className="space-y-1">
          <Label className="text-xs">Date & Time</Label>
          <Input
            type="datetime-local"
            value={round.scheduled_datetime}
            onChange={(e) => onUpdate('scheduled_datetime', e.target.value)}
            className="h-9 w-64"
          />
        </div>

        {/* Assessment Checkpoints */}
        <div className="space-y-2">
          <Label className="text-xs font-semibold">Assessment Checkpoints</Label>
          <div className="space-y-3">
            {round.checkpoints.map((cp, cpIdx) => (
              <div key={cpIdx} className="p-3 rounded-lg border bg-slate-50 space-y-2">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium text-sm">{cp.name}</p>
                    {cp.description && (
                      <p className="text-xs text-slate-500">{cp.description}</p>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => onUpdateCheckpoint(cpIdx, 'met', true)}
                      className={`px-3 py-1 rounded-l-full text-xs font-medium transition-colors ${
                        cp.met === true
                          ? 'bg-emerald-500 text-white'
                          : 'bg-slate-200 text-slate-600 hover:bg-emerald-100'
                      }`}
                    >
                      <Check className="h-3 w-3 inline mr-1" />
                      Met
                    </button>
                    <button
                      onClick={() => onUpdateCheckpoint(cpIdx, 'met', false)}
                      className={`px-3 py-1 rounded-r-full text-xs font-medium transition-colors ${
                        cp.met === false
                          ? 'bg-red-500 text-white'
                          : 'bg-slate-200 text-slate-600 hover:bg-red-100'
                      }`}
                    >
                      <X className="h-3 w-3 inline mr-1" />
                      Not Met
                    </button>
                  </div>
                </div>
                <Input
                  value={cp.notes}
                  onChange={(e) => onUpdateCheckpoint(cpIdx, 'notes', e.target.value)}
                  placeholder="Add notes..."
                  className="h-8 text-sm"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Feedback */}
        <div className="space-y-1">
          <Label className="text-xs font-semibold">Detailed Feedback</Label>
          <Textarea
            value={round.feedback}
            onChange={(e) => onUpdate('feedback', e.target.value)}
            placeholder="Overall assessment, recommendations, next steps..."
            rows={3}
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default { HRAssessmentConfigPanel, AddPerformanceDialog, BriefingSessionCard, DemoRoundCard };
